/**
 * MFA-setup-klient för Lyktan (S-04-14).
 *
 * Wrappar `/api/admin/mfa/setup` och `/api/admin/mfa/verify` (S-03-07,
 * MfaRoutes.kt) som tillsammans utgör admin-aktiveringsflödet för TOTP.
 *
 *   - `POST /api/admin/mfa/setup`  → 200 `{ otpauthUri, secretBase32 }`
 *   - `POST /api/admin/mfa/verify` → 204 (success), 401/409/429 + felkod
 *
 * Hard-rules som tillämpas:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body.
 *  - CSRF-header på alla muterande anrop — speglar `harden_admin_csrf`-cookien
 *    (S-12-11c) via `csrfHeader()`.
 *  - secretBase32 / otpauthUri returneras till anroparen och förväntas
 *    kasseras vid view-unmount. Klienten lagrar dem ALDRIG i
 *    sessionStorage / localStorage.
 *  - Inga loggar av otpauthUri eller secretBase32 — de innehåller TOTP-secret
 *    i klartext och får inte hamna i konsolen eller i felrapporter.
 *  - Felmeddelanden mappas till svenska användartexter.
 *
 * Observera: backend-svaret för `/setup` använder camelCase-fält
 * (`otpauthUri`, `secretBase32`) eftersom MfaSetupResponse i Kotlin är
 * deklarerad utan @SerialName-override. Vi matchar wire-formatet 1:1 här
 * — ingen snake_case-konvertering behövs på MFA-endpointerna.
 */

import { csrfHeader } from '../auth/csrf';

const API_BASE = '/api/admin/mfa';

export type SetupResult =
  | { kind: 'ok'; otpauthUri: string; secretBase32: string }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type VerifyResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'invalid_code' }
  | { kind: 'not_configured' }
  | { kind: 'locked' }
  | { kind: 'failure'; message: string };

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att aktivera tvåfaktor.';
    case 'invalid_request':
      return 'Ogiltig begäran. Kontrollera koden och försök igen.';
    case 'invalid_mfa_code':
      return 'Felaktig kod, försök igen.';
    case 'mfa_locked':
      return 'För många misslyckade försök. Vänta en stund och försök igen.';
    case 'mfa_not_configured':
      return 'Ingen aktiv MFA-uppsättning hittades. Börja om från steg 1.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawSetupResponse = {
  otpauthUri?: unknown;
  secretBase32?: unknown;
};

/**
 * POST /api/admin/mfa/setup — genererar nytt TOTP-secret + otpauth-URI.
 *
 * Endpointen kan anropas flera gånger; varje anrop nollställer ev tidigare
 * (icke-aktiverat) secret. Klienten ska därför bara anropa det när admin
 * aktivt valt att starta uppsättningen.
 *
 * Returnerar `{ otpauthUri, secretBase32 }` som båda innehåller secret-bytena
 * i klartext — kasseras av anroparen efter rendering / verify.
 */
export async function requestSetup(): Promise<SetupResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/setup`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...csrfHeader() },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let raw: RawSetupResponse;
  try {
    raw = (await response.json()) as RawSetupResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (typeof raw.otpauthUri !== 'string' || typeof raw.secretBase32 !== 'string') {
    return { kind: 'failure', message: messageForError('default') };
  }
  // OBS: Vi loggar ALDRIG raw eller dess fält. Värdena vidarebefordras till
  // anroparen i return-värdet och förväntas inte ekas i någon konsol-output.
  return { kind: 'ok', otpauthUri: raw.otpauthUri, secretBase32: raw.secretBase32 };
}

/**
 * POST /api/admin/mfa/verify — verifierar 6-siffrig TOTP-kod och aktiverar
 * MFA-secret vid första lyckade kontroll. Backend skriver `mfa_activate`-rad
 * i `admin_actions` (S-03-07) och svarar 204 No Content.
 *
 * Klienten validerar inte koden lokalt — backend återkopplar med opaque
 * felkod (`invalid_mfa_code`, `mfa_locked`, etc) som mappas till svenska
 * användartexter.
 */
export async function verifyAndEnable(totpCode: string): Promise<VerifyResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({ code: totpCode }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 204 || response.ok) {
    return { kind: 'ok' };
  }

  const body = await safeJson(response);
  const error = body?.error;

  if (response.status === 401 && error === 'invalid_mfa_code') {
    return { kind: 'invalid_code' };
  }
  if (response.status === 401 || response.status === 403) {
    if (error === 'invalid_mfa_code') {
      return { kind: 'invalid_code' };
    }
    return { kind: 'unauthorized' };
  }
  if (response.status === 409 || error === 'mfa_not_configured') {
    return { kind: 'not_configured' };
  }
  if (response.status === 429 || error === 'mfa_locked') {
    return { kind: 'locked' };
  }
  return { kind: 'failure', message: messageForError(error ?? 'default') };
}

/**
 * Mappar verify-resultatet till användartext. Exporteras så vyer kan
 * presentera enhetlig text utan att duplicera mappningen.
 */
export function verifyResultMessage(result: VerifyResult): string | null {
  switch (result.kind) {
    case 'ok':
      return null;
    case 'invalid_code':
      return messageForError('invalid_mfa_code');
    case 'not_configured':
      return messageForError('mfa_not_configured');
    case 'locked':
      return messageForError('mfa_locked');
    case 'unauthorized':
      return messageForError('unauthorized');
    case 'failure':
      return result.message;
  }
}
